import axios from "axios";
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { getConfiguredStudyId, getStudySlug } from "../helper/study";
import { getWelcomeInfo } from "../services/weclome.service";
import { getCurrentUser, logout } from "../services/user.service";

import { UserType } from "../types/session";
import { ProfileType } from "../types/profile";
import { useConfig } from "./ConfigContext";
import i18n from "../i18n";



const TOKEN_KEY_NAME = "resonance_access_token";

interface AuthState {
  user: UserType | null;
  isInitialized: boolean;
}

interface AuthContextValue extends AuthState {
  // Resolves to the user's ACTUAL role (matched by user.role_id against the
  // welcome roles) so post-login redirects can be role-aware immediately — the
  // landing's pre-selected role can be wrong (the API allows logging in through
  // another role's entry page), and reading selectedRole from context inside the
  // same handler would be stale anyway.
  startSession: (token: string) => Promise<ProfileType | undefined>;
  endSession: () => Promise<void>;
  getCurrent: () => Promise<void>;
  isAuthenticated: boolean;
  // True while a signup flow is actively running. The landing routes use it to
  // NOT auto-redirect to the app when `isAuthenticated` flips mid-signup (which
  // happens at the consent step), so the flow can finish on the success page and
  // navigate via its own button — the way it did before single-study mode.
  signupInProgress: boolean;
  setSignupInProgress: (value: boolean) => void;
}

interface AuthProviderProps {
  children: ReactElement;
}

const setSession = (accessToken: string | null): void => {
  if (accessToken) {
    localStorage.setItem(TOKEN_KEY_NAME, accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem(TOKEN_KEY_NAME);
    delete axios.defaults.headers.common.Authorization;
  }
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isInitialized: false,
  startSession: () => Promise.resolve(undefined),
  endSession: () => Promise.resolve(),
  getCurrent: () => Promise.resolve(),
  isAuthenticated: false,
  signupInProgress: false,
  setSignupInProgress: () => {},
});

/**
 * 
 * @param obj 
 * @returns 
 * @description check if all first level fields in "obj" empty or not.
 */
const areAllFieldsEmpty = (obj: Record<string, any>): boolean => {
  return Object.values(obj).every(value => {
    return value === null || value === undefined || String(value).trim() === '';
  });
};

export const AuthProvider = ({ children }: AuthProviderProps): ReactElement => {
  const [user, setUser] = useState<UserType | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [signupInProgress, setSignupInProgress] = useState<boolean>(false);
  const { study, setRole, clearRoleIfMismatch } = useConfig();

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem(TOKEN_KEY_NAME);

        // Restore the session whenever a valid token exists — even if the URL carries
        // an "invitation=" param. (Previously the invitation param skipped this branch,
        // which logged out an already-authenticated user who opened an invite link.)
        // New, token-less invitees still fall through to the else branch and see signup.
        if (accessToken) {
          const slug = getStudySlug();
          // welcome is still needed here to map user.role_id -> role (and as the id
          // source in multi-study mode); in single-study mode the id is the pinned one.
          // Fetch in the user's UI language: the role object feeds localized screens
          // (signup steps, profile) and the slug-keyed welcome cache — an "en" fetch
          // here used to poison both for non-English users.
          const {
            data: { id: welcomeId, roles },
          } = await getWelcomeInfo({
            slug,
            lang: i18n.resolvedLanguage ?? i18n.language ?? "en",
          });
          const id = getConfiguredStudyId() ?? welcomeId;
          console.log("access token found.");
          setSession(accessToken);
          const { data: user } = await getCurrentUser(id);

          if (!areAllFieldsEmpty(user.attributes)) { 
            console.log("user set correctly");
            const role = roles.find((r) => r.id === user.role_id);
            setUser(user);
            setRole(role);
        }
        } else {
          console.log("version 2.0");
          console.log("no access token");
          setUser(null);
          setRole(null);
        }
      } catch (err) {
        console.log("user error", err);
        window.localStorage.removeItem(TOKEN_KEY_NAME);
        setUser(null);
      } finally {
        setTimeout(function () {
          setIsInitialized(true);
        }, 100);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.id]);

  // A session whose row still owes a signup is NOT a logged-in user: it exists only to
  // carry the inscription journey. The old test — "does this row have any identity
  // value?" — was a guess that an invitation defeats, because the inviter fills in the
  // invitee's name (and, for a dossier invite, their numéro) before they ever register.
  // Without the server's own answer, invited people were dropped straight into the app
  // with no password, no consent and no questionnaires.
  const isAuthenticated =
    !!user && !areAllFieldsEmpty(user.attributes) && !user.registration_pending;

  const startSession = async (token: string): Promise<ProfileType | undefined> => {
    setSession(token);
    const { data: user } = await getCurrentUser(study.id);
    console.log("starting session with user", user);
    setUser(user);
    // Commit the user's REAL role (the landing pre-selection can be another
    // role's — cross-role login is allowed API-side). Fetched in the user's UI
    // LANGUAGE: this role object feeds localized screens (signup inclusion/site/
    // secret-question texts, profile) — an "en" fetch would clobber them.
    // getWelcomeInfo is deduped and cached, so no meaningful extra latency.
    try {
      const {
        data: { roles },
      } = await getWelcomeInfo({
        slug: getStudySlug(),
        lang: i18n.resolvedLanguage ?? i18n.language ?? "en",
      });
      const role = roles.find((r) => r.id === user.role_id);
      if (role) {
        setRole(role);
      } else {
        // No matching role: clear the pre-selection IF it's provably not the
        // user's — the check reads FRESH context state (functional update),
        // never this closure, so a same-tick signup pick can't be clobbered.
        clearRoleIfMismatch(user.role_id);
      }
      return role;
    } catch (e) {
      console.log("startSession role resolution failed", e);
      // Resolution failed: keep a MATCHING pre-selection (mid-signup screens
      // depend on it), clear a provably-wrong one (cross-role login).
      clearRoleIfMismatch(user.role_id);
      return undefined;
    }
  };

  const getCurrent = async (): Promise<void> => {
    try {
      const { data: user } = await getCurrentUser(study.id);
      setUser(user);
    } catch (e) {}
  };

  const endSession = async (): Promise<void> => {
    try {
      if (user)
        await logout() 
    } catch (e) {
    } finally {
      setSession(null);
      setUser(null);
      setRole(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isInitialized,
        isAuthenticated,
        startSession,
        endSession,
        getCurrent,
        signupInProgress,
        setSignupInProgress,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
